/**
 * DocumentSection Component
 *
 * Displays documents attached to an entity.
 * PDF preview is always visible. Download/open actions appear on hover.
 */

import { useEffect, useState } from 'react';
import { FileText, Download } from 'lucide-react';
import { Skeleton, PDFPreview, EmptyState } from '@/components/ui';
import { useFormatDate } from '@/hooks';
import { DOCUMENT_TYPE_LABELS, type DocumentType } from '@bstock/shared';
import { documentsService } from '../services/documents.service';
import { useEntityDocuments } from '../hooks/useDocuments';
import { downloadFile } from '../utils/download';
import type { DocumentResponse } from '@bstock/shared';

interface DocumentSectionProps {
  entityRoute: string;
  entityId: string;
  /** Skip the outer border-t / mt wrapper (used when embedded in a flex layout) */
  inline?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface DocumentCardProps {
  document: DocumentResponse;
  entityRoute: string;
  entityId: string;
  formatDateShort: (date: string) => string;
}

function DocumentCard({ document, entityRoute, entityId, formatDateShort }: DocumentCardProps) {
  const [url, setUrl] = useState<string | null>(null);
  const isPdf = document.mimeType === 'application/pdf' || document.fileName.toLowerCase().endsWith('.pdf');
  const label = DOCUMENT_TYPE_LABELS[document.documentType as DocumentType] ?? document.fileName;

  useEffect(() => {
    documentsService.getDownloadUrl(entityRoute, entityId, document.id)
      .then(setUrl)
      .catch(() => {});
  }, [entityRoute, entityId, document.id]);

  const openInNewTab = () => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const download = () => {
    if (url) downloadFile(url, document.fileName).catch(() => {});
  };

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden bg-white">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="shrink-0 p-2 bg-primary-50 rounded-lg">
          <FileText className="w-4 h-4 text-primary-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-dark-500 truncate">{label}</p>
          <p className="text-xs text-neutral-500">
            {document.fileName} · {formatFileSize(document.fileSize)} · {formatDateShort(document.createdAt)}
          </p>
        </div>
      </div>

      {/* PDF preview — toujours visible */}
      {isPdf && url && (
        <div className="border-t border-neutral-200 p-4">
          <PDFPreview
            fileUrl={url}
            onDownload={download}
            onOpenInNewTab={openInNewTab}
          />
        </div>
      )}

      {isPdf && !url && (
        <div className="border-t border-neutral-200 p-4">
          <Skeleton className="h-[600px] w-[500px] rounded-lg" />
        </div>
      )}

      {/* Non-PDF: download button */}
      {!isPdf && (
        <div className="border-t border-neutral-200 px-4 py-3">
          <button
            type="button"
            onClick={download}
            disabled={!url}
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="w-4 h-4" />
            Telecharger
          </button>
        </div>
      )}
    </div>
  );
}

export function DocumentSection({ entityRoute, entityId, inline = false }: DocumentSectionProps) {
  const { data: documents, isLoading } = useEntityDocuments(entityRoute, entityId);
  const { formatDateShort } = useFormatDate();

  const wrapperClass = inline ? '' : 'mt-6 pt-6 border-t border-neutral-200';

  if (isLoading) {
    return (
      <div className={wrapperClass}>
        <h3 className="text-sm font-medium text-neutral-700 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Documents
        </h3>
        <Skeleton className="h-[200px] rounded-lg" />
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <div className={wrapperClass}>
        <h3 className="text-sm font-medium text-neutral-700 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Documents
        </h3>
        <EmptyState
          icon={FileText}
          title="Aucun document"
          description="Aucun document n'est rattache a cet element"
          size="sm"
        />
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <h3 className="text-sm font-medium text-neutral-700 mb-3 flex items-center gap-2">
        <FileText className="w-4 h-4" />
        Documents
        <span className="ml-1 text-xs font-normal text-neutral-400">({documents.length})</span>
      </h3>
      <div className="space-y-4">
        {documents.map((doc) => (
          <DocumentCard
            key={doc.id}
            document={doc}
            entityRoute={entityRoute}
            entityId={entityId}
            formatDateShort={formatDateShort}
          />
        ))}
      </div>
    </div>
  );
}
